import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Plus, Pencil, Trash2, ShieldCheck, LifeBuoy, Mail, Phone, MapPin, Tag, HardHat, X, Share2, RotateCcw, FileDown, Send,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";
import { ClientPayLinkRow } from "@/components/ClientPayLinkRow";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ASSET_STATUSES = ["active", "expired", "replaced", "returned", "decommissioned"];
const TYPE_OPTIONS = ["laptop", "desktop", "mobile", "server", "network", "peripheral", "electronic", "other"];

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((d - today) / 86400000);
  } catch { return null; }
}

function WarrantyPill({ end, label, icon: Icon }) {
  const days = daysUntil(end);
  if (!end) return <span className="text-xs text-muted-foreground">—</span>;
  const expired = days !== null && days < 0;
  const soon = days !== null && days >= 0 && days <= 30;
  const cls = expired
    ? "bg-red-50 text-red-700 ring-red-200"
    : soon
    ? "bg-amber-50 text-amber-700 ring-amber-200"
    : "bg-emerald-50 text-emerald-700 ring-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${cls}`}>
      <Icon className="size-3" />
      {label}: {fmtDate(end)} {days !== null && (
        <span className="mono text-[10px] opacity-80">
          ({expired ? `${Math.abs(days)}d ago` : `${days}d`})
        </span>
      )}
    </span>
  );
}

function emptyAsset() {
  return {
    name: "", specification: "", type: "", brand: "", model: "",
    serial_number: "", origin: "Manual",
    delivered_at: new Date().toISOString().slice(0, 10),
    unit_price: 0,
    warranty_months: 0, support_months: 0,
    warranty_start: "", warranty_end: "",
    support_start: "", support_end: "",
    location: "", notes: "", status: "active",
    item_id: null,
  };
}

export default function ClientDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [statementOpen, setStatementOpen] = useState(false);

  // Resolve client from list (no dedicated detail endpoint needed for this MVP).
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const c = useMemo(() => clients.find((x) => x.id === id) || null, [clients, id]);

  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: api.listItems });
  const { data: users = [] } = useQuery({
    queryKey: ["users-directory"],
    queryFn: () => api.usersDirectory(),
  });
  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["client-assets", id],
    queryFn: () => api.listClientAssets(id),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAsset());
  const [confirmId, setConfirmId] = useState(null);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const portalSecurityMut = useMutation({
    mutationFn: (login_required) => api.setClientPortalSecurity(id, login_required),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(r.login_required
        ? "Portal login wall enabled — clients must verify their email"
        : "Portal login wall disabled — link opens directly");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update portal security"),
  });
  const revokeMut = useMutation({
    mutationFn: () => api.revokeClientPortalLink(id),
    onSuccess: async (r) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      setRevokeOpen(false);
      if (r?.portal_url) {
        let copied = false;
        try {
          await navigator.clipboard.writeText(r.portal_url);
          copied = true;
        } catch {}
        toast.success(copied ? "Old link revoked — fresh portal link copied to clipboard" : "Old link revoked — new portal link ready", {
          description: r.portal_url,
          duration: 12000,
        });
      } else {
        toast.success("Old link revoked — a new token was generated");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not revoke portal link"),
  });

  const createMut = useMutation({
    mutationFn: (body) => api.createClientAsset(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-assets", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setForm(emptyAsset()); toast.success("Asset created");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ aid, body }) => api.updateAsset(aid, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-assets", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); setEditing(null); setForm(emptyAsset()); toast.success("Asset updated");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const deleteMut = useMutation({
    mutationFn: (aid) => api.deleteAsset(aid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client-assets", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirmId(null); toast.success("Deleted");
    },
  });

  const engineersMut = useMutation({
    mutationFn: (site_engineer_ids) =>
      api.updateClient(id, { site_engineer_ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["clients-for-attendance"] });
      toast.success("Site engineers updated");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to update engineers"),
  });

  const assignedIds = Array.isArray(c?.site_engineer_ids) ? c.site_engineer_ids : [];
  const assignedUsers = useMemo(
    () => users.filter((u) => assignedIds.includes(u.id)),
    [users, assignedIds],
  );
  const unassignedUsers = useMemo(
    () => users.filter((u) => !assignedIds.includes(u.id)),
    [users, assignedIds],
  );
  const [engineerPick, setEngineerPick] = useState("");

  function assignEngineer(uid) {
    if (!uid || assignedIds.includes(uid)) return;
    engineersMut.mutate([...assignedIds, uid]);
    setEngineerPick("");
  }
  function unassignEngineer(uid) {
    engineersMut.mutate(assignedIds.filter((x) => x !== uid));
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyAsset());
    setOpen(true);
  }
  function openEdit(a) {
    setEditing(a);
    setForm({
      ...emptyAsset(),
      ...a,
      delivered_at: (a.delivered_at || "").slice(0, 10),
      warranty_start: (a.warranty_start || "").slice(0, 10),
      warranty_end: (a.warranty_end || "").slice(0, 10),
      support_start: (a.support_start || "").slice(0, 10),
      support_end: (a.support_end || "").slice(0, 10),
    });
    setOpen(true);
  }
  function onItemPick(item_id) {
    const it = items.find((x) => x.id === item_id);
    if (!it) return setForm((f) => ({ ...f, item_id: null }));
    setForm((f) => ({
      ...f,
      item_id,
      name: it.name,
      specification: it.specification || "",
      type: it.type || f.type,
      brand: it.brand || f.brand,
      model: it.model || f.model,
      unit_price: Number(it.price || 0),
      warranty_months: Number(it.warranty_months || 0),
      support_months: Number(it.support_months || 0),
    }));
  }
  function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("item_name") + " *"); return; }
    const body = {
      ...form,
      unit_price: Number(form.unit_price || 0),
      warranty_months: Number(form.warranty_months || 0),
      support_months: Number(form.support_months || 0),
    };
    if (editing) updateMut.mutate({ aid: editing.id, body });
    else createMut.mutate(body);
  }

  if (!c) {
    return (
      <div data-testid="client-detail-page">
        <Link to="/clients" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
          <ArrowLeft className="size-3.5 mr-1" /> {t("back_to_clients")}
        </Link>
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="size-3.5 mr-1" /> {t("back_to_clients")}
      </Link>

      <PageHeader
        overline={t("client_details")}
        title={c.name}
        subtitle={c.contact_person ? `${c.contact_person}` : null}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="client-statement-pdf-btn"
              variant="outline"
              onClick={() => api.openClientStatementPdf(c.id)}
            >
              <FileDown className="size-4 mr-1.5" /> Statement PDF
            </Button>
            <Button
              data-testid="client-statement-email-btn"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              onClick={() => setStatementOpen(true)}
            >
              <Send className="size-4 mr-1.5" /> Email statement
            </Button>
            <Button
              data-testid="copy-portal-link-btn"
              variant="outline"
              onClick={async () => {
                try {
                  const r = await api.ensureClientPortalLink(c.id);
                  if (!r?.portal_url) {
                    toast.error("PUBLIC_APP_URL is not configured on the server");
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(r.portal_url);
                    toast.success("Portal link copied — share it with the client");
                  } catch {
                    toast.info(r.portal_url, { duration: 15000 });
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to generate portal link");
                }
              }}
            >
              <Share2 className="size-4 mr-1.5" />
              Client portal link
            </Button>
          </div>
        }
      />

      <StatementDialog
        key={statementOpen ? `stmt-${c.id}` : "stmt-closed"}
        client={statementOpen ? c : null}
        onClose={() => setStatementOpen(false)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="border border-border rounded-md bg-white p-5 lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {c.email && (
            <p className="flex items-center gap-2 text-muted-foreground"><Mail className="size-4" /> <span className="mono">{c.email}</span></p>
          )}
          {c.phone && (
            <p className="flex items-center gap-2 text-muted-foreground"><Phone className="size-4" /> <span className="mono">{c.phone}</span></p>
          )}
          {c.address && (
            <p className="flex items-start gap-2 text-muted-foreground sm:col-span-2"><MapPin className="size-4 mt-0.5" /> {c.address}</p>
          )}
          {c.notes && (
            <p className="sm:col-span-2 text-xs text-muted-foreground border-t border-border pt-3 mt-1 whitespace-pre-wrap">
              <span className="overline mr-2">{t("notes")}</span>{c.notes}
            </p>
          )}
          <ClientPayLinkRow client={c} />
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <p className="overline mb-2">{t("total_assets")}</p>
          <p className="font-display font-bold text-3xl">{assets.length}</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="overline">{t("status_active")}</p>
              <p className="mono font-medium text-emerald-700">{assets.filter((a) => a.status === "active").length}</p>
            </div>
            <div>
              <p className="overline">{t("status_expired")}</p>
              <p className="mono font-medium text-red-600">{assets.filter((a) => a.status === "expired").length}</p>
            </div>
          </div>
        </div>
      </div>

      <div
        className="border border-border rounded-md bg-white p-5 mb-6"
        data-testid="portal-security-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className={`size-5 mt-0.5 ${c.portal_login_required ? "text-emerald-600" : "text-muted-foreground"}`} />
            <div>
              <p className="font-medium text-sm">Portal access security</p>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                {c.portal_login_required
                  ? `Login wall is ON — visitors must verify a one-time code sent to ${c.email || "the client email on file"}.`
                  : "Login wall is OFF — anyone with the link can open the portal."}
                {!c.email && c.portal_login_required && (
                  <span className="text-red-600 font-medium"> No email on file — clients cannot log in.</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs font-medium">
              <Switch
                data-testid="portal-login-toggle"
                checked={!!c.portal_login_required}
                disabled={portalSecurityMut.isPending}
                onCheckedChange={(v) => portalSecurityMut.mutate(v)}
              />
              Require email login
            </label>
            <Button
              data-testid="portal-revoke-btn"
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setRevokeOpen(true)}
            >
              <RotateCcw className="size-3.5 mr-1.5" /> Revoke link
            </Button>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-md bg-white p-5 mb-6" data-testid="site-engineers-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="overline flex items-center gap-1.5">
              <HardHat className="size-3.5 text-primary" /> Site Engineers
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Users authorised to log attendance at this client&apos;s site. The
              Attendance form will scope the engineer dropdown to this list.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={engineerPick}
              onValueChange={(v) => assignEngineer(v)}
            >
              <SelectTrigger data-testid="site-engineer-picker" className="w-56">
                <SelectValue placeholder="Add engineer…" />
              </SelectTrigger>
              <SelectContent>
                {unassignedUsers.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    All users are already assigned
                  </div>
                )}
                {unassignedUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}{" "}
                    <span className="text-[10px] text-muted-foreground">
                      ({u.role})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {assignedUsers.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No site engineers assigned yet — the attendance form will show all
            active users as a fallback.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {assignedUsers.map((u) => (
              <span
                key={u.id}
                data-testid={`site-engineer-chip-${u.id}`}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 text-xs font-medium"
              >
                <HardHat className="size-3" />
                {u.name}
                <span className="text-[9px] uppercase tracking-wider text-red-500/80">
                  {u.role}
                </span>
                <button
                  data-testid={`site-engineer-remove-${u.id}`}
                  className="rounded-full hover:bg-red-100 p-0.5 text-red-600"
                  onClick={() => unassignEngineer(u.id)}
                  title="Remove"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="overline">{t("client_assets")}</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{t("client_assets")}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t("client_assets_hint")}</p>
        </div>
        <Button data-testid="new-asset-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
          <Plus className="size-4 mr-1.5" /> {t("new_asset")}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : assets.length === 0 ? (
        <EmptyState
          title={t("no_data")}
          hint={t("new_asset")}
          action={
            <Button data-testid="empty-new-asset-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_asset")}
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto" data-testid="assets-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">{t("item_name")}</TableHead>
                <TableHead className="overline">{t("asset_type")}</TableHead>
                <TableHead className="overline">{t("brand")} / {t("model")}</TableHead>
                <TableHead className="overline">{t("serial_number")}</TableHead>
                <TableHead className="overline">{t("origin")}</TableHead>
                <TableHead className="overline">{t("warranty")} / {t("support")}</TableHead>
                <TableHead className="overline">{t("status")}</TableHead>
                <TableHead className="overline text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((a) => (
                <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                  <TableCell className="font-medium">
                    {a.name}
                    {a.specification && <div className="text-[11px] text-muted-foreground">{a.specification}</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {a.type ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200 text-[11px]">
                        <Tag className="size-3" /> {a.type}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {[a.brand, a.model].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell className="mono text-xs">{a.serial_number || "—"}</TableCell>
                  <TableCell className="mono text-xs text-muted-foreground">
                    {a.delivery_order_id ? (
                      <Link to={`/delivery-orders/${a.delivery_order_id}`} className="text-primary hover:underline">
                        {a.origin}
                      </Link>
                    ) : (a.origin || "—")}
                  </TableCell>
                  <TableCell className="space-y-1">
                    <WarrantyPill end={a.warranty_end} label={t("warranty")} icon={ShieldCheck} />
                    <div />
                    <WarrantyPill end={a.support_end} label={t("support")} icon={LifeBuoy} />
                  </TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${
                      a.status === "active" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" :
                      a.status === "expired" ? "bg-red-50 text-red-700 ring-red-200" :
                      "bg-slate-100 text-slate-700 ring-slate-200"
                    }`}>{t("status_" + a.status)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`edit-asset-${a.id}`} variant="ghost" size="sm" onClick={() => openEdit(a)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button data-testid={`delete-asset-${a.id}`} variant="ghost" size="sm" onClick={() => setConfirmId(a.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="asset-form-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? t("edit_asset") : t("new_asset")}</DialogTitle>
            <DialogDescription className="text-xs">
              Track a delivered or owned asset with warranty &amp; support periods.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="overline">{t("link_to_item")}</Label>
              <Select value={form.item_id || undefined} onValueChange={onItemPick}>
                <SelectTrigger data-testid="asset-form-item"><SelectValue placeholder={t("select_item")} /></SelectTrigger>
                <SelectContent>
                  {items.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.name} {it.brand ? `· ${it.brand}` : ""} {it.model ? `· ${it.model}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label className="overline">{t("item_name")} *</Label>
              <Input data-testid="asset-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("specification")}</Label>
              <Textarea data-testid="asset-form-spec" value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} rows={2} />
            </div>

            <div>
              <Label className="overline">{t("asset_type")}</Label>
              <Select value={form.type || undefined} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="asset-form-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((tp) => (
                    <SelectItem key={tp} value={tp}>{tp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">{t("serial_number")}</Label>
              <Input data-testid="asset-form-serial" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("brand")}</Label>
              <Input data-testid="asset-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("model")}</Label>
              <Input data-testid="asset-form-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("origin")}</Label>
              <Input data-testid="asset-form-origin" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="Manual / DO-... / Vendor" />
            </div>
            <div>
              <Label className="overline">{t("location")}</Label>
              <Input data-testid="asset-form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>

            <div>
              <Label className="overline">{t("delivery_date")}</Label>
              <DatePicker testId="asset-form-delivered-at" value={form.delivered_at} onChange={(v) => setForm({ ...form, delivered_at: v || "" })} />
            </div>
            <div>
              <Label className="overline">{t("price")}</Label>
              <Input data-testid="asset-form-price" type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} />
            </div>

            <div className="sm:col-span-2 border-t border-border pt-3 mt-1">
              <p className="overline">{t("warranty")}</p>
            </div>
            <div>
              <Label className="overline">{t("warranty_months")}</Label>
              <Input data-testid="asset-form-warranty-months" type="number" min="0" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="overline">{t("warranty_start")}</Label>
                <DatePicker testId="asset-form-warranty-start" value={form.warranty_start} onChange={(v) => setForm({ ...form, warranty_start: v || "" })} />
              </div>
              <div>
                <Label className="overline">{t("warranty_end")}</Label>
                <DatePicker testId="asset-form-warranty-end" value={form.warranty_end} onChange={(v) => setForm({ ...form, warranty_end: v || "" })} />
              </div>
            </div>

            <div className="sm:col-span-2 border-t border-border pt-3 mt-1">
              <p className="overline">{t("support")}</p>
            </div>
            <div>
              <Label className="overline">{t("support_months")}</Label>
              <Input data-testid="asset-form-support-months" type="number" min="0" value={form.support_months} onChange={(e) => setForm({ ...form, support_months: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="overline">{t("support_start")}</Label>
                <DatePicker testId="asset-form-support-start" value={form.support_start} onChange={(v) => setForm({ ...form, support_start: v || "" })} />
              </div>
              <div>
                <Label className="overline">{t("support_end")}</Label>
                <DatePicker testId="asset-form-support-end" value={form.support_end} onChange={(v) => setForm({ ...form, support_end: v || "" })} />
              </div>
            </div>

            <div>
              <Label className="overline">{t("asset_status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="asset-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t("status_" + s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("notes")}</Label>
              <Textarea data-testid="asset-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>

            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="asset-form-cancel">
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="asset-form-save" className="bg-primary hover:bg-primary/90">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-asset-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-asset-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <AlertDialogContent data-testid="portal-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this client&apos;s portal link?</AlertDialogTitle>
            <AlertDialogDescription>
              The current link stops working immediately and every active portal
              session is signed out. A fresh link is generated and copied to your
              clipboard so you can re-share it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="portal-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="portal-revoke-confirm"
              onClick={() => revokeMut.mutate()}
              className="bg-red-600 hover:bg-red-700"
            >
              Revoke &amp; regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatementDialog({ client, onClose }) {
  const [to, setTo] = useState(client?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (!client) return null;

  async function send() {
    if (!to.trim() || !to.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setBusy(true);
    try {
      const r = await api.emailClientStatement(client.id, {
        to: to.trim(),
        message: message.trim(),
      });
      toast.success(`Statement sent to ${r.to} — ${r.invoice_count} outstanding invoice(s)`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send statement");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!client} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="statement-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Email statement — {client.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Sends a branded Statement of Account PDF listing this client&apos;s
            outstanding invoices with the balance due.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">Recipient *</Label>
            <Input
              data-testid="statement-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
            />
          </div>
          <div>
            <Label className="overline">Message (optional)</Label>
            <Textarea
              data-testid="statement-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Friendly reminder ahead of month-end…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="statement-cancel">
            Cancel
          </Button>
          <Button
            data-testid="statement-send"
            className="bg-amber-600 hover:bg-amber-700"
            disabled={busy}
            onClick={send}
          >
            <Send className="size-4 mr-1.5" /> {busy ? "Sending…" : "Send statement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
